import { isNumber } from 'lodash-es'
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FunctionComponent,
  type ReactNode,
} from 'react'
import {
  PlaceholderEmbed,
  type PlaceholderEmbedProps,
} from 'react-social-media-embed'
import { useMount } from 'react-use'
import styled from 'styled-components'
import { v4 } from 'uuid'
import type { Optional } from '@/types/optional'

const embedJsScriptSrc = 'https://www.instagram.com/embed.js'
const minPlaceholderWidth = 328
const defaultPlaceholderHeight = 372
const borderRadius = 3

enum EmbedStage {
  CheckScript = 'check-script',
  LoadScript = 'load-script',
  ProcessEmbed = 'process-embed',
  EmbedSuccess = 'embed-success',
  Error = 'error',
}

interface ThreadsEmbedProps {
  url: string
  width?: string | number
  height?: string | number
  linkText?: string
  captioned?: boolean
  placeholderImageUrl?: string
  placeholderSpinner?: ReactNode
  placeholderSpinnerDisabled?: boolean
  placeholderProps?: PlaceholderEmbedProps
  embedPlaceholder?: ReactNode
  placeholderDisabled?: boolean
  scriptLoadDisabled?: boolean
  retryDisabled?: boolean
  igVersion?: string
}

const ThreadsEmbed: FunctionComponent<ThreadsEmbedProps> = ({
  url,
  width,
  height,
  linkText = 'View post on Threads',
  captioned = false,
  placeholderImageUrl,
  placeholderSpinner,
  placeholderSpinnerDisabled = false,
  placeholderProps,
  embedPlaceholder,
  placeholderDisabled = false,
  scriptLoadDisabled = false,
  retryDisabled = false,
  igVersion = '14',
}) => {
  const [iframeHeight, setIframeHeight] = useState<string>('auto')
  const [stage, setStage] = useState(EmbedStage.CheckScript)
  const uuidRef = useRef(v4())
  const embedContainerKey = useMemo(
    () => `${uuidRef.current}-${Date.now()}`,
    [],
  )

  const cleanUrlWithEndingSlash = `${url}${url.endsWith('/') ? '' : '/'}`
  const isPercentageWidth = !!width?.toString().includes('%')

  // Check Script Stage
  useEffect(() => {
    if (stage !== EmbedStage.CheckScript) {
      return
    }

    if (window?.instgrm?.Embeds?.process) {
      setStage(EmbedStage.ProcessEmbed)
    } else if (!scriptLoadDisabled) {
      setStage(EmbedStage.LoadScript)
    }
  }, [scriptLoadDisabled, stage])

  // Load Script Stage
  useEffect(() => {
    if (stage !== EmbedStage.LoadScript) {
      return
    }

    const script = document.createElement('script')
    script.src = embedJsScriptSrc
    script.defer = true

    const handleLoad = (): void => {
      script.removeEventListener('load', handleLoad)
      script.removeEventListener('error', handleError)
      setStage(EmbedStage.ProcessEmbed)
    }

    const handleError = (): void => {
      script.removeEventListener('load', handleLoad)
      script.removeEventListener('error', handleError)
      if (!retryDisabled) {
        setStage(EmbedStage.CheckScript)
      } else {
        setStage(EmbedStage.Error)
      }
    }

    script.addEventListener('load', handleLoad)
    script.addEventListener('error', handleError)
    document.head.appendChild(script)

    return (): void => {
      script.removeEventListener('load', handleLoad)
      script.removeEventListener('error', handleError)
    }
  }, [stage, retryDisabled])

  // Process Embed Stage
  useEffect(() => {
    if (stage !== EmbedStage.ProcessEmbed) {
      return
    }

    const process = window?.instgrm?.Embeds?.process

    if (process) {
      try {
        process()

        const checkInterval = window.setInterval(() => {
          const container = document.getElementById(embedContainerKey)

          const iframe = container?.querySelector<HTMLIFrameElement>(
            'iframe[id^="instagram-embed-"]',
          )

          if (iframe?.src?.includes(cleanUrlWithEndingSlash)) {
            window.clearInterval(checkInterval)
          }
        }, 100)

        const cleanup = (): void => {
          if (checkInterval) {
            window.clearInterval(checkInterval)
          }
        }

        return cleanup
      } catch (error) {
        if (!retryDisabled) {
          setStage(EmbedStage.CheckScript)
        } else {
          setStage(EmbedStage.Error)
        }
      }
    } else if (!retryDisabled) {
      setStage(EmbedStage.CheckScript)
    } else {
      setStage(EmbedStage.Error)
    }
  }, [stage, retryDisabled, cleanUrlWithEndingSlash, embedContainerKey])

  // Setup message event listener for height updates from Instagram embed
  useMount(() => {
    // Listen for messages from the iframe to get its height
    const messageHandler = (event: MessageEvent): void => {
      // Filter messages not from threads.com
      if (event.origin && !/threads\.com$/.test(event.origin)) {
        return
      }

      if (!isNumber(event.data)) {
        return
      }

      setIframeHeight(`${event.data}px`)
      setStage(EmbedStage.EmbedSuccess)
    }

    window.addEventListener('message', messageHandler)

    return () => {
      window.removeEventListener('message', messageHandler)
    }
  })

  const placeholder = embedPlaceholder ?? (
    <PlaceholderEmbed
      imageUrl={placeholderImageUrl}
      linkText={linkText}
      spinner={placeholderSpinner}
      spinnerDisabled={placeholderSpinnerDisabled}
      url={cleanUrlWithEndingSlash}
      {...placeholderProps}
      style={{
        ...{
          position: 'absolute',
          top: 0,
          left: 0,
          minWidth: isPercentageWidth ? undefined : minPlaceholderWidth,
          width: width ?? '100%',
          height: height ?? defaultPlaceholderHeight,
          border: '1px solid #dee2e6',
          borderRadius,
        },
        ...placeholderProps?.style,
      }}
    />
  )

  return (
    <ThreadsEmbedContainer
      $height={height}
      $iframeHeight={iframeHeight}
      $width={width}
      id={embedContainerKey}
    >
      {stage !== EmbedStage.EmbedSuccess && (
        <blockquote
          className='instagram-media'
          data-instgrm-captioned={captioned ? captioned : undefined}
          data-instgrm-permalink={`${cleanUrlWithEndingSlash}?utm_source=ig_embed&utm_campaign=loading`}
          data-instgrm-version={igVersion}
          data-width={isPercentageWidth ? '100%' : (width ?? undefined)}
          key={embedContainerKey}
        >
          {!placeholderDisabled && placeholder}
        </blockquote>
      )}
      {stage === EmbedStage.Error && (
        <div style={{ padding: '1rem', textAlign: 'center', color: '#666' }}>
          Failed to load Threads post
        </div>
      )}
    </ThreadsEmbedContainer>
  )
}

const ThreadsEmbedContainer = styled.div<{
  $width?: string | number
  $height?: string | number
  $iframeHeight: string
}>`
  position: relative;
  width: ${({ $width }): Optional<string | number> => $width ?? undefined};
  height: ${({ $height }): Optional<string | number> => $height ?? 'auto'};
  min-height: ${({ $height }): Optional<string | number> =>
    $height ?? defaultPlaceholderHeight}px;
  border-radius: ${borderRadius}px;
  padding: 10px;

  .instagram-media {
    position: relative !important;
    width: 100% !important;
    min-height: ${({ $iframeHeight }): string => $iframeHeight} !important;
    height: auto !important;
    border: none !important;
    margin: 0 !important;
    padding: 0 !important;
    overflow: visible !important;
  }

  iframe {
    width: 100% !important;
    min-width: 326px !important;
    max-width: none !important;
    min-height: inherit !important;
    height: auto !important;
    margin: 0 !important;
    padding: 0 !important;
    display: block !important;
  }

  .rsme-d-none {
    display: none !important;
  }
`

export default ThreadsEmbed
